import React, { useState, useEffect, useContext, useRef } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, sp, type } from "../../constants/styles";
import Ic from "../common/Ic";
import { uploadPhoto } from "../../utils/supabaseClient";

const AUDIENCES = [
  { v: "all", l: "Everyone" },
  { v: "landlord", l: "Landlords" },
  { v: "tenant", l: "Tenants" },
  { v: "service", l: "Service" },
];

// A few festive accent presets for one-tap color choice.
const COLOR_PRESETS = ["#7c3aed", "#dc2626", "#ea580c", "#d97706", "#16a34a", "#0891b2", "#db2777"];

/* iOS-style switch */
function Switch({ on, onClick, busy, h }) {
  return (
    <button
      role="switch" aria-checked={on} onClick={onClick} disabled={busy}
      style={{
        width: 48, height: 28, borderRadius: 14, border: "none",
        background: on ? h.gn : (h.b2 || "#8e8e9340"), position: "relative",
        cursor: busy ? "default" : "pointer", transition: "background 0.2s", flexShrink: 0,
        opacity: busy ? 0.6 : 1,
      }}>
      <span style={{ position: "absolute", top: 3, left: on ? 23 : 3, width: 22, height: 22, borderRadius: 11, background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
    </button>
  );
}

function Field({ label, children, h }) {
  return (
    <div style={{ marginBottom: sp.md }}>
      <label style={{ ...type.xs, color: h.ts || h.sc, fontWeight: 600, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</label>
      {children}
    </div>
  );
}

export default function AdminCampaign() {
  const { h, go, flash } = useContext(Ctx);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Greeting state
  const [gEnabled, setGEnabled] = useState(false);
  const [gTitle, setGTitle] = useState("");
  const [gMessage, setGMessage] = useState("");
  const [gImage, setGImage] = useState("");
  const [gLink, setGLink] = useState("");
  const [gColor, setGColor] = useState("#7c3aed");
  const [gAudience, setGAudience] = useState("all");
  const [gId, setGId] = useState("");

  // Discount state
  const [dEnabled, setDEnabled] = useState(false);
  const [dText, setDText] = useState("");
  const [dPercent, setDPercent] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.adminGetCampaignConfig();
        if (res?.success && res.config) {
          const g = res.config.greeting || {};
          const d = res.config.discount || {};
          setGEnabled(!!g.enabled); setGTitle(g.title || ""); setGMessage(g.message || "");
          setGImage(g.imageUrl || ""); setGLink(g.linkUrl || ""); setGColor(g.color || "#7c3aed"); setGAudience(g.audience || "all");
          setGId(g.id || "");
          setDEnabled(!!d.enabled); setDText(d.text || ""); setDPercent(d.percent || 0);
        }
      } catch { flash("Failed to load campaign settings", "error"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPickImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPhoto(file, "campaign", { maxWidth: 1000, quality: 0.85 });
      setGImage(url);
      flash("Image uploaded");
    } catch (err) { flash(err?.message || "Upload failed", "error"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Bump the campaign id whenever the greeting is enabled and the
      // admin saves — so a re-run re-shows the popup to everyone. We use
      // a fresh stamp each save while enabled; keep existing when off.
      const nextId = gEnabled ? String(Date.now()) : gId;
      const payload = {
        greeting: { enabled: gEnabled, id: nextId, title: gTitle.trim(), message: gMessage.trim(), imageUrl: gImage, linkUrl: gLink.trim(), color: gColor, audience: gAudience },
        discount: { enabled: dEnabled, text: dText.trim(), percent: parseInt(dPercent, 10) || 0 },
      };
      const res = await api.adminSetCampaignConfig(payload);
      if (res?.success) { setGId(nextId); flash("Saved — broadcast is live"); }
      else flash(res?.message || "Save failed", "error");
    } catch { flash("Network error", "error"); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: sp.xl, color: h.ts || h.sc }}>Loading…</div>;

  const inp = { width: "100%", boxSizing: "border-box", background: h.ib, border: `1px solid ${h.b2 || h.bd}`, borderRadius: 10, padding: "10px 12px", color: h.tx, fontSize: 14, outline: "none" };

  return (
    <div style={{ paddingTop: sp.lg, paddingBottom: 40 }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Ic n="arrow-left" s={14} c={h.ac} /> Back
      </button>
      <h2 style={{ ...type.h1, color: h.tx, margin: "0 0 4px" }}>🎉 Festival & Offers</h2>
      <div style={{ color: h.ts || h.sc, fontSize: 12, marginBottom: sp.lg }}>
        Broadcast a festival greeting to users, or a discount banner to landlords.
      </div>

      {/* ── FESTIVAL GREETING ── */}
      <div style={{ ...crd(h), padding: sp.lg, marginBottom: sp.lg }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: sp.md }}>
          <div>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 15 }}>Festival Greeting</div>
            <div style={{ color: h.ts || h.sc, fontSize: 11 }}>Popup on open + banner, for the chosen audience</div>
          </div>
          <Switch on={gEnabled} onClick={() => setGEnabled(v => !v)} busy={saving} h={h} />
        </div>

        <Field label="Audience" h={h}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {AUDIENCES.map(a => (
              <button key={a.v} onClick={() => setGAudience(a.v)}
                style={{ padding: "6px 12px", borderRadius: 999, border: `1.5px solid ${gAudience === a.v ? h.ac : h.bd}`, background: gAudience === a.v ? `${h.ac}12` : "transparent", color: gAudience === a.v ? h.ac : (h.ts || h.sc), cursor: "pointer", fontSize: 12, fontWeight: gAudience === a.v ? 700 : 500 }}>
                {a.l}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Title" h={h}>
          <input value={gTitle} onChange={e => setGTitle(e.target.value)} placeholder="e.g. Happy Diwali! 🪔" maxLength={120} style={inp} />
        </Field>

        <Field label="Message" h={h}>
          <textarea value={gMessage} onChange={e => setGMessage(e.target.value)} placeholder="e.g. Wishing you and your family a bright and prosperous Diwali!" rows={3} maxLength={600} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
        </Field>

        <Field label="Image (optional)" h={h}>
          {gImage ? (
            <div style={{ position: "relative", display: "inline-block" }}>
              <img src={gImage} alt="greeting" style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 10, border: `1px solid ${h.bd}`, display: "block" }} />
              <button onClick={() => setGImage("")} style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", borderRadius: 999, width: 24, height: 24, cursor: "pointer", fontSize: 14 }}>×</button>
            </div>
          ) : (
            <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading}
              style={{ width: "100%", padding: "14px", borderRadius: 10, border: `1.5px dashed ${h.b2 || h.bd}`, background: h.sf, color: h.ts || h.sc, cursor: uploading ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>
              {uploading ? "Uploading…" : "＋ Upload festival image"}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} style={{ display: "none" }} />
        </Field>

        <Field label="Banner link (optional)" h={h}>
          <input value={gLink} onChange={e => setGLink(e.target.value)} placeholder="https://your-store.com/diwali-offer" maxLength={600} style={inp} inputMode="url" />
          <div style={{ color: h.tt || h.sc, fontSize: 10, marginTop: 5, lineHeight: 1.45 }}>
            Tapping the banner image opens this link in the browser. Use it for your own products / website only.
            <b style={{ color: h.wn }}> Don't</b> link to an outside payment for this app's plan — Apple/Google require in-app purchase for that.
          </div>
        </Field>

        <Field label="Decoration color" h={h}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {COLOR_PRESETS.map(c => (
              <button key={c} onClick={() => setGColor(c)} aria-label={c}
                style={{ width: 28, height: 28, borderRadius: "50%", background: c, border: gColor === c ? `3px solid ${h.tx}` : `2px solid ${h.bd}`, cursor: "pointer" }} />
            ))}
            <input type="color" value={gColor} onChange={e => setGColor(e.target.value)} style={{ width: 36, height: 28, border: "none", background: "none", cursor: "pointer" }} />
          </div>
        </Field>

        {/* Live preview */}
        <div style={{ marginTop: sp.md }}>
          <div style={{ ...type.xs, color: h.ts || h.sc, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 }}>Preview</div>
          <div style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${h.bd}`, background: `linear-gradient(160deg, ${gColor}22 0%, ${gColor}08 100%)`, padding: 18, textAlign: "center" }}>
            {gImage && <img src={gImage} alt="" style={{ maxWidth: "100%", maxHeight: 140, borderRadius: 12, marginBottom: 12, display: "inline-block" }} />}
            <div style={{ color: gColor, fontWeight: 800, fontSize: 18, marginBottom: 6 }}>{gTitle || "Your title here"}</div>
            <div style={{ color: h.tx, fontSize: 13, lineHeight: 1.5 }}>{gMessage || "Your festive message here…"}</div>
          </div>
        </div>
      </div>

      {/* ── LANDLORD DISCOUNT ── */}
      <div style={{ ...crd(h), padding: sp.lg, marginBottom: sp.lg }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: sp.md }}>
          <div>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 15 }}>Landlord Discount Banner</div>
            <div style={{ color: h.ts || h.sc, fontSize: 11 }}>Shown to landlords; taps to the Upgrade screen</div>
          </div>
          <Switch on={dEnabled} onClick={() => setDEnabled(v => !v)} busy={saving} h={h} />
        </div>
        <Field label="Banner text" h={h}>
          <input value={dText} onChange={e => setDText(e.target.value)} placeholder="e.g. 🎉 Diwali Offer: 20% off all plans!" maxLength={200} style={inp} />
        </Field>
        <Field label="Discount % off (applied at plan checkout)" h={h}>
          <input type="number" min={0} max={90} value={dPercent}
            onChange={e => setDPercent(Math.max(0, Math.min(90, parseInt(e.target.value, 10) || 0)))}
            placeholder="e.g. 20" style={{ ...inp, width: 120 }} />
        </Field>
        <div style={{ color: h.tt || h.sc, fontSize: 10, lineHeight: 1.5 }}>
          The banner text is marketing. The <b>Discount %</b> is the real cut (0–90) applied to <b>every paid plan's</b>
          {" "}checkout amount, server-side — the actual charge reflects it. Leave it <b>0</b> to keep the banner display-only.
        </div>
        {dText.trim() && (
          <div style={{ marginTop: sp.md, background: `${h.ac}12`, border: `1px solid ${h.ac}30`, borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>🏷️</span>
            <span style={{ color: h.ac, fontWeight: 600, fontSize: 13, flex: 1 }}>{dText}</span>
            <span style={{ color: h.ac, fontSize: 16, fontWeight: 700 }}>{"›"}</span>
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving || uploading}
        style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: (saving || uploading) ? `${h.ac}99` : h.ac, color: "#fff", fontSize: 15, fontWeight: 700, cursor: (saving || uploading) ? "default" : "pointer" }}>
        {saving ? "Saving…" : "Save & Broadcast"}
      </button>
    </div>
  );
}
